import React, { useState, useEffect } from "react"
import { DropdownButton, Dropdown } from "react-bootstrap"

import { withFavouritesConfig } from "../config/FavouriteConfigProvider"
import { withGraphState } from "../config/GraphConfigProvider"
import FavouriteEditor from "./FavouriteEditor"
import FavouritesManager from "./FavouritesManager"
import { getFormattedMessage, isLTPM } from "../../components/shared_components_registration"

export const CertificateIcon = ({ gs, intl }) => {
  const pages = (gs?.graph_interfaces || []).map((config) => config.name).join(", ")
  return (
    <>
      {gs.graph_interfaces && gs.graph_interfaces.length > 0 && (
        <span
          className="fa fa-certificate"
          aria-hidden="true"
          title={getFormattedMessage(
            "favourites.default.graph.configuration.for",
            `Default graph configuration for ${pages}`,
            true,
            intl,
            { pages: pages },
          )}
        />
      )}
    </>
  )
}

const FavouritesSelector = ({
  view,
  onSelect,
  loadGraphSet,
  updateRunList,
  graphSet,
  runs,
  registerFavouriteGraphSet,
  registerFavouriteRunSet,
  should_match_view = true, // 'true' indicates that the dashboard calling this can only display certain graph faves
  favouriteGraphSets = [],
  favouriteRunSets = [],
  dropToggleClass,
  intl,
  restoreDefaultGraphs,
  default_graph_configs = [],
}) => {
  const getText = (id, msg) => getFormattedMessage(id, msg, true, intl)
  const registration = {
    favourite_graphs_label: getText("favourites.favourite.graphs", "Favourite Graphs"),
    favourite_runs_label: getText("favourites.favourite.runs", "Favourite Runs"),
    favourite_save_fail_message: getText("favourites.failed.to.save", "Failed to save favourites"),
  }
  const [showingFavouriteGraphsCreation, setShowingFavouriteGraphsCreation] = useState(false)
  const [showingFavouriteRunsCreation, setShowingFavouriteRunsCreation] = useState(false)
  const [showingManager, setShowingManager] = useState(false)
  const [availableGraphFavorites, setAvailableGraphFavorites] = useState([])

  useEffect(() => {
    const baseGraphFaves = favouriteGraphSets.filter((g) => !g.trashed)
    setAvailableGraphFavorites(
      should_match_view ? baseGraphFaves.filter((gs) => gs.view === view) : baseGraphFaves,
    )
  }, [favouriteGraphSets])

  const hideFavouriteCreation = () => {
    setShowingFavouriteGraphsCreation(false)
    setShowingFavouriteRunsCreation(false)
    setShowingManager(false)
  }

  const selectFavouriteGraphSet = (favourite) => {
    if (!favourite) return // Ignore "Add" buttons.

    if (favourite.graphConfigs) {
      onSelect ? onSelect() : null // Any dash specific extra functions to run in case of fav selection
      loadGraphSet(() => [...favourite.graphConfigs])
    } else if (favourite.runs) {
      if (updateRunList) {
        updateRunList([...favourite.runs])
      }
    }
  }

  const checkGraphSetName = (name) => {
    return (favouriteGraphSets || []).some((gs) => gs.name === name)
  }

  const checkRunReactorSetName = (name) => {
    return (favouriteRunSets || []).some((rrs) => rrs.name === name)
  }

  const createFavouriteGraphs = (name, description, replace, interfaces = []) => {
    registerFavouriteGraphSet(name, [...graphSet], description, view, replace, interfaces).catch((err) => {
      alert(registration.favourite_save_fail_message)
    })

    hideFavouriteCreation()
  }

  const createFavouriteRuns = (name, description, replace) => {
    registerFavouriteRunSet(name, [...runs], description, replace).catch((err) => {
      alert(registration.favourite_save_fail_message)
    })

    hideFavouriteCreation()
  }

  return (
    <React.Fragment>
      {showingFavouriteGraphsCreation && (
        <FavouriteEditor
          getText={getText}
          title={getText("favourites.create.graph.set", "Create Favourite Graph Set")}
          encoded={graphSet}
          show={showingFavouriteGraphsCreation}
          replaceChoices={availableGraphFavorites}
          onHide={hideFavouriteCreation}
          onCreate={createFavouriteGraphs}
          nameCheck={checkGraphSetName}
        />
      )}
      {view === "Standard" && showingFavouriteRunsCreation && (
        <FavouriteEditor
          getText={getText}
          title={getText("favourites.create.run.set", "Create Favourite Run Set")}
          show={showingFavouriteRunsCreation}
          replaceChoices={favouriteRunSets}
          onHide={hideFavouriteCreation}
          onCreate={createFavouriteRuns}
          nameCheck={checkRunReactorSetName}
        />
      )}
      {showingManager && (
        <FavouritesManager intl={intl} show={showingManager} onHide={hideFavouriteCreation} />
      )}

      <DropdownButton
        className={`favourites-dropdown ${dropToggleClass || ""}`}
        id={"favourite-plots"}
        variant={"outline-primary"}
        title={<span className="fa fa-star" aria-hidden="true" />}
      >
        <Dropdown.Item onClick={() => setShowingFavouriteGraphsCreation(true)}>
          <span className="fa fa-plus" aria-hidden="true" />
          &nbsp;
          <span>{registration.favourite_graphs_label}</span>
        </Dropdown.Item>
        {view === "Standard" && (
          <Dropdown.Item onClick={() => setShowingFavouriteRunsCreation(true)}>
            <span className="fa fa-plus" aria-hidden="true" />
            &nbsp;
            <span>{registration.favourite_runs_label}</span>
          </Dropdown.Item>
        )}
        <Dropdown.Item onClick={() => setShowingManager(true)}>
          <span className="fa fa-star" aria-hidden="true" />
          &nbsp;
          <span>{getText("favourites.manage.favourites", "Manage Favourites")}</span>
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => {
            onSelect ? onSelect() : null
            view === "Standard" ? restoreDefaultGraphs("graphs") : restoreDefaultGraphs("live_graphs")
          }}
        >
          <span className="fa fa-undo" aria-hidden="true" />
          &nbsp;
          {getText("favourites.restore.default.graphs", "Restore Default Graph Config")}
        </Dropdown.Item>

        <Dropdown.Divider />

        {(availableGraphFavorites.length > 0 || (default_graph_configs.length > 0 && isLTPM)) && (
          <>
            {default_graph_configs.length > 0 && isLTPM && (
              <>
                {default_graph_configs.map((g, i) => {
                  return (
                    <Dropdown.Item
                      key={`defaultFave${i}`}
                      eventKey={g.name}
                      onClick={() => selectFavouriteGraphSet({ graphConfigs: g.graphConfigs })}
                    >
                      <span className="fa fa-thumb-tack" aria-hidden="true" />
                      &nbsp;
                      <span className="fa fa-bar-chart" aria-hidden="true" />
                      &nbsp;
                      {g.name}
                    </Dropdown.Item>
                  )
                })}
                <hr />
              </>
            )}
            <div style={{ paddingLeft: "10px", paddingBottom: "2px" }}>
              {registration.favourite_graphs_label}:
            </div>
            {availableGraphFavorites.map((gs, idx) => (
              <Dropdown.Item key={`g${idx}`} eventKey={gs} onClick={() => selectFavouriteGraphSet(gs)}>
                {gs.graph_interfaces.length ? (
                  <>
                    <CertificateIcon gs={gs} intl={intl} />
                    &nbsp;
                    <span className="fa fa-bar-chart" aria-hidden="true" />
                  </>
                ) : (
                  <span className="fa fa-bar-chart" aria-hidden="true" />
                )}
                &nbsp;
                {gs.name}
                &nbsp;
                {gs.view ? ` (${gs.view})` : null}
              </Dropdown.Item>
            ))}
            <Dropdown.Divider />
          </>
        )}

        {view === "Standard" && favouriteRunSets.length > 0 && (
          <>
            <div style={{ paddingLeft: "10px", paddingBottom: "2px" }}>
              {registration.favourite_runs_label}:
            </div>
            {favouriteRunSets.map((rrs, idx) => (
              <Dropdown.Item key={`r${idx}`} eventKey={rrs} onClick={() => selectFavouriteGraphSet(rrs)}>
                <span className="fa fa-thumb-tack" aria-hidden="true" /> {rrs.name}
              </Dropdown.Item>
            ))}
          </>
        )}
      </DropdownButton>
    </React.Fragment>
  )
}

export default withGraphState(withFavouritesConfig(FavouritesSelector))
